/* eslint-disable @typescript-eslint/prefer-for-of */
import { useState, useCallback } from 'react';
import { getRandomRows } from './functions';
import { TileProps, defaultGameConfig, GameConfigProps } from './GameConfigContext';

export interface HookProps {
  gameConfig: GameConfigProps;
  setGameConfig: (_: GameConfigProps) => void;
  createTiles: (gameConfigObj?: GameConfigProps) => void;
  showTileByXY: (x: number, y: number, copyTiles: TileProps[][]) => { tiles: TileProps[][], gameOver: boolean, flagCount: number } | TileProps[][];
  setFlagged: (x: number, y: number, flag: boolean) => void;
  loadNewGame: () => void;
}

const useGameConfig = (): HookProps => {
  const [gameConfig, setGameConfigState] = useState(defaultGameConfig);
  const setGameConfig = useCallback((_gameConfig: GameConfigProps) => {
    setGameConfigState(_gameConfig);
  }, []);

  const createTiles = (gameConfigObj?: GameConfigProps) => {
    const copyTiles: TileProps[][] = [];
    const gc = gameConfigObj ? gameConfigObj : gameConfig;
    const bombRows = getRandomRows(gc.areaTilesWidthNum * gc.areaTilesHeightNum, gc.bombCount);

    for (let i = 0; i < gc.areaTilesHeightNum; i++) {
      if (!copyTiles[i]) copyTiles[i] = [];
      for (let j = 0; j < gc.areaTilesWidthNum; j++) {
        const currentNumRow = (i * gc.areaTilesWidthNum) + j;
        const isBomb = bombRows.indexOf(currentNumRow) >= 0;
        const tile: TileProps = {
          hidden: true,
          bomb: isBomb,
          bombsNear: isBomb ? -1 : 0,
          flagged: false,
        }
        copyTiles[i][j] = tile;
      }
    }

    for (let x = 0; x < gc.areaTilesHeightNum; x++) {
      for (let y = 0; y < gc.areaTilesWidthNum; y++) {
        let countBombsNear = 0;
        if (copyTiles[x-1] && copyTiles[x-1][y-1]) {
          if (copyTiles[x-1][y-1].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x] && copyTiles[x][y-1]) {
          if (copyTiles[x][y-1].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x+1] && copyTiles[x+1][y-1]) {
          if (copyTiles[x+1][y-1].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x-1] && copyTiles[x-1][y]) {
          if (copyTiles[x-1][y].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x+1] && copyTiles[x+1][y]) {
          if (copyTiles[x+1][y].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x] && copyTiles[x][y+1]) {
          if (copyTiles[x][y+1].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x-1] && copyTiles[x-1][y+1]) {
          if (copyTiles[x-1][y+1].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x+1] && copyTiles[x+1][y+1]) {
          if (copyTiles[x+1][y+1].bomb) {
            countBombsNear++;
          }
        }

        if (!copyTiles[x][y].bomb) {
          copyTiles[x][y].bombsNear = countBombsNear;
        }
      }
    }
    console.log(copyTiles);
    setGameConfigState({...gc, tiles: copyTiles});
  };

  const setFlagged = (x: number, y: number, flag: boolean) => {
    const copyTiles = [...gameConfig.tiles];
    copyTiles[x][y].flagged = flag;
    setGameConfig({...gameConfig, tiles: copyTiles});
  }

  const showRows: {x: number; y: number}[] = [];
  const showTileByXY2 = (x: number, y: number): void => {
    if (gameConfig.tiles[x][y].bombsNear === 0) {
      for (let xx = Math.max(x-1, 0); xx <= Math.min(x+1, gameConfig.areaTilesHeightNum - 1); xx++) {
        for(let yy = Math.max(y-1, 0); yy <= Math.min(y+1, gameConfig.areaTilesWidthNum - 1); yy++) {
          if (xx === x && yy === y) continue;
          if (showRows.findIndex((row) => row.x === xx && row.y === yy) === -1) {
            showRows.push({x: xx, y: yy});
            showTileByXY2(xx, yy);
          }
        }
      }
    }
  }

  const showTileByXY = (x: number, y: number, copyTiles: TileProps[][], deep = 0): { tiles: TileProps[][], gameOver: boolean, flagCount: number } | TileProps[][] => {
    copyTiles[x][y].hidden = false;

    const output: { tiles: TileProps[][], gameOver: boolean, flagCount: number } = {
      gameOver: false,
      tiles: [],
      flagCount: gameConfig.flagCount,
    }

    // Jeżeli trafiono na bombę
    if (copyTiles[x][y].bomb) {
      // Odkryj wszystkie bomby na boardzie
      output.tiles = copyTiles.map((tile:TileProps[]) => tile.map((til:TileProps) => {
        return til.bomb ? {...til, hidden: false} : til;
      }));
      // Ustaw game over
      output.gameOver = true;
      return output;
    }

    showRows.splice(0, showRows.length);
    showTileByXY2(x, y);

    for (const row of showRows) {
      copyTiles[row.x][row.y].hidden = false;
      if (copyTiles[row.x][row.y].flagged) {
        output.flagCount--;
      }
    }

    output.tiles = copyTiles;
    return output;
  }

  const loadNewGame = () => {
    const newGame = {...gameConfig};
    newGame.correctCount = 0;
    newGame.flagCount = 0;
    newGame.gameOver = false;
    newGame.win = false;
    newGame.started = false;
    newGame.tiles = [];
    newGame.reloaded = !newGame.reloaded;
    createTiles(newGame);
  }

  return {
    gameConfig,
    setGameConfig,
    createTiles,
    showTileByXY,
    setFlagged,
    loadNewGame,
  };
};

export default useGameConfig;
